import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Calendar, Eye, Tag, Clock } from 'lucide-react';
import SEO from '../components/SEO';
import { LoadingPage } from '../components/LoadingSpinner';

const BlogPage = () => {
  const [posts, setPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTag, setSelectedTag] = useState('');
  const [tags, setTags] = useState([]);

  const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
  const API = `${BACKEND_URL}/api/blog`;

  useEffect(() => {
    fetchPosts();
    fetchTags();
  }, [selectedTag]);

  const fetchPosts = async () => {
    try {
      setLoading(true);
      let url = `${API}?published_only=true&limit=20`;
      if (selectedTag) {
        url += `&tag=${selectedTag}`;
      }
      
      const response = await fetch(url);
      const data = await response.json();
      setPosts(data);
    } catch (err) {
      setError('Failed to load blog posts');
      console.error('Error fetching posts:', err);
    } finally {
      setLoading(false);
    }
  };

  const fetchTags = async () => {
    try {
      const response = await fetch(`${API}/tags/list`);
      const data = await response.json();
      setTags(data);
    } catch (err) {
      console.error('Error fetching tags:', err);
    }
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    });
  };

  if (loading && posts.length === 0) {
    return <LoadingPage message="Loading blog posts..." />;
  }

  return (
    <div className="bg-white min-h-screen">
      <SEO 
        title="News & Insights"
        description="Latest news, insights, and expertise from Lawman Commercials. Stay updated with MAN truck industry trends, maintenance tips, and commercial vehicle insights from our experienced team."
        keywords="MAN trucks news, commercial vehicle insights, truck maintenance tips, Leicester truck dealer news, automotive industry updates"
        canonical="/blog"
      />
      
      {/* Hero Section */}
      <section className="bg-black text-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center">
            <h1 className="text-4xl lg:text-5xl font-black mb-6 font-inter">
              News & <span className="text-orange-500">Updates</span>
            </h1>
            <p className="text-xl text-gray-300 max-w-3xl mx-auto font-inter">
              Stay up to date with the latest news, industry insights, and updates from Lawman Commercials.
            </p>
          </div>
        </div>
      </section>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="grid grid-cols-1 lg:grid-cols-4 gap-8">
          {/* Sidebar */}
          <div className="lg:col-span-1">
            <div className="bg-gray-50 rounded-lg p-6 sticky top-6">
              <h3 className="font-bold text-black mb-4 font-inter">Categories</h3>
              
              <div className="space-y-2">
                <button
                  onClick={() => setSelectedTag('')}
                  className={`w-full text-left px-3 py-2 rounded-lg font-inter transition-colors ${
                    selectedTag === '' 
                      ? 'bg-orange-600 text-white' 
                      : 'text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  All Posts ({posts.length})
                </button>
                
                {tags.map((tagObj) => (
                  <button
                    key={tagObj.tag}
                    onClick={() => setSelectedTag(tagObj.tag)}
                    className={`w-full text-left px-3 py-2 rounded-lg font-inter transition-colors flex justify-between items-center ${
                      selectedTag === tagObj.tag
                        ? 'bg-orange-600 text-white'
                        : 'text-gray-700 hover:bg-gray-200'
                    }`}
                  >
                    <span>{tagObj.tag}</span>
                    <span className="text-xs">{tagObj.count}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>

          {/* Main Content */}
          <div className="lg:col-span-3">
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-4 mb-6">
                <p className="text-red-600 font-inter">{error}</p>
              </div>
            )}

            {posts.length === 0 && !loading ? (
              <div className="text-center py-12">
                <h3 className="text-xl font-bold text-gray-700 mb-2 font-inter">No posts found</h3>
                <p className="text-gray-600 font-inter">
                  {selectedTag ? `No posts found for "${selectedTag}"` : 'No blog posts available yet.'}
                </p>
              </div>
            ) : (
              <div className="space-y-8">
                {posts.map((post) => (
                  <article key={post.id} className="bg-white border border-gray-200 rounded-lg overflow-hidden hover:shadow-lg transition-shadow">
                    {post.featured_image && (
                      <img 
                        src={post.featured_image} 
                        alt={post.title}
                        className="w-full h-64 object-cover"
                      />
                    )}
                    
                    <div className="p-6">
                      <div className="flex items-center gap-4 text-sm text-gray-600 mb-3">
                        <div className="flex items-center gap-1">
                          <Calendar className="h-4 w-4" />
                          <span>{formatDate(post.createdAt)}</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Eye className="h-4 w-4" />
                          <span>{post.views} views</span>
                        </div>
                        <div className="flex items-center gap-1">
                          <Clock className="h-4 w-4" />
                          <span>{post.author}</span>
                        </div>
                      </div>
                      
                      <h2 className="text-2xl font-bold text-black mb-3 font-inter">
                        <Link 
                          to={`/blog/${post.id}`}
                          className="hover:text-orange-600 transition-colors"
                        >
                          {post.title}
                        </Link>
                      </h2>
                      
                      <p className="text-gray-600 mb-4 font-inter leading-relaxed">
                        {post.excerpt}
                      </p>
                      
                      {post.tags && post.tags.length > 0 && (
                        <div className="flex items-center gap-2 mb-4">
                          <Tag className="h-4 w-4 text-gray-400" />
                          <div className="flex gap-2 flex-wrap">
                            {post.tags.map((tag) => (
                              <button
                                key={tag}
                                onClick={() => setSelectedTag(tag)}
                                className="bg-gray-100 hover:bg-orange-100 text-gray-700 hover:text-orange-700 px-2 py-1 rounded text-xs font-inter transition-colors"
                              >
                                {tag}
                              </button>
                            ))}
                          </div>
                        </div>
                      )}
                      
                      <Link 
                        to={`/blog/${post.id}`}
                        className="inline-block bg-orange-600 hover:bg-orange-700 text-white px-6 py-2 rounded-lg font-bold transition-colors"
                      >
                        Read More
                      </Link>
                    </div>
                  </article>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default BlogPage;